import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, formattedCurrency, withStyles, BillPayTransactionResponse, Card } from 'vopay';
import { TransactionCard, Title, DetailsRows } from 'UI';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type TransactionInfoProps = {
  transaction: BillPayTransactionResponse;
};

type DataProps = {
  hasErrorInfo: boolean;
  failureReason: string;
  accountId: string;
};

const BillPayInfo: FC<TransactionInfoProps> = ({ transaction }: TransactionInfoProps) => {
  const { state } = useLocation<DataProps | undefined>();

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.BillPayInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  const transactionDetails = [
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: tGlobal('Transaction.TransactionHistory.transactionTypes.Bill Pay', {
        defaultValue: 'Bill Pay',
      }),
    },
    {
      label: t('TransactionDetails.transactionID', { defaultValue: 'Transaction ID' }),
      value: transaction.TransactionID,
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transaction.Note || '-',
    },
  ];

  const payeeDetails = [
    {
      label: t('AccountHolderDetails.payee', { defaultValue: 'Payee' }),
      value: transaction.MerchantName || transaction.Nickname || '-',
    },
    {
      label: t('AccountHolderDetails.nickname', { defaultValue: 'Nickname' }),
      value: transaction.Nickname || '-',
    },
    {
      label: t('AccountHolderDetails.paymentAccount', { defaultValue: 'Account Number' }),
      value: transaction.PaymentAccount || '-',
    },
    {
      label: t('AccountHolderDetails.category', { defaultValue: 'Category' }),
      value: transaction.MerchantCategory || '-',
      visible: false, //TODO: For next sprint make this is the correct value
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={transaction.MerchantName}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        type={'Bill Pay'}
        failureReason={state?.failureReason || ''}
        contactId={state?.accountId}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsHeader', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('bankAccountDetails', { defaultValue: 'Bank Account Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={payeeDetails} />
      </Card>

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoHeader', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default BillPayInfo;
